import { memo, MouseEvent, useEffect, useRef, useState } from 'react';
import ReactFileReader from 'react-file-reader';
import { useTranslation } from 'react-i18next';
import { useStore, useUnit } from 'effector-react';
import {
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextareaAutosize,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PhotoIcon from '@mui/icons-material/Photo';
import SendIcon from '@mui/icons-material/Send';
import {
  $attachmentsLoading,
  $chatData,
  $forwardedMessages,
  $isHidden,
  $messageInput,
  changeIsHidden,
  changeMessageInput,
  forwardMessage,
  sendMessage,
  setAttachments,
} from '@features/socket-chats-reworked/models';
import { $showOnlyInMainChat } from '../../../models/chat-tabs';
import { chatShowOnlyMssages } from '../../../types/chat-tabs';
import { Upload } from '../../upload';
import { ForwardMessages } from './forwardMessages';
import { useStyles } from './styles';

export const ChatInput = memo(() => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const messageInput = useStore($messageInput);
  const chatData = useStore($chatData);
  const isHidden = useStore($isHidden);
  const attachmentsLoading = useStore($attachmentsLoading);
  const classes = useStyles({ isHidden });
  const [forwardedMessages, showOnlyInMainChat] = useUnit([
    $forwardedMessages,
    $showOnlyInMainChat,
  ]);

  const { t } = useTranslation();

  const containerRef = useRef(null);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMessageSend = () => {
    if (!chatData) return;

    if (messageInput) {
      sendMessage({
        channel_key: chatData.id,
        text: messageInput,
        is_hidden: isHidden,
      });
    }

    forwardedMessages.forEach(({ id }) => {
      forwardMessage({
        channel_key: chatData.id,
        message_id: id,
      });
    });
  };

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      handleMessageSend();
      e.preventDefault();
    }

    if (e.shiftKey && e.key === 'Enter') {
      e.preventDefault();
      changeMessageInput(`${messageInput}\n`);
    }

    return null;
  };

  const handleChangeMessageInput = (e: any) => {
    changeMessageInput(e.target.value);
  };

  const handleFiles = (files: any) => {
    setAttachments({ mode: 'file', files: files });
    handleClose();
  };

  const handleImageFiles = (files: any) => {
    setAttachments({ mode: 'image', files: files });
    handleClose();
  };

  useEffect(() => {
    if (!showOnlyInMainChat || showOnlyInMainChat === chatShowOnlyMssages.notHidden) {
      changeIsHidden(false);

      return;
    }

    if (showOnlyInMainChat === chatShowOnlyMssages.hidden) {
      changeIsHidden(true);

      return;
    }
  }, [showOnlyInMainChat]);

  return (
    <div className={classes.chatInput__wrapper} ref={containerRef}>
      <Upload containerRef={containerRef} />

      <div className={classes.chatInput}>
        <ForwardMessages />

        <div className={classes.chatInput__content}>
          <TextareaAutosize
            className={classes.chatInput__input}
            value={messageInput}
            placeholder={t('Message') as string}
            maxRows={4}
            onChange={handleChangeMessageInput}
            onKeyDown={handleKeyDown}
          />

          {attachmentsLoading ? (
            <CircularProgress
              className={classes.chatInput__icon}
              color="primary"
              size={24}
            />
          ) : (
            <IconButton className={classes.chatInput__icon} onClick={handleClick}>
              <AttachFileIcon />
            </IconButton>
          )}

          <IconButton color="primary" onClick={handleMessageSend}>
            <SendIcon className={classes.chatInput__icon} />
          </IconButton>

          <Menu
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
            container={containerRef.current}
          >
            <ReactFileReader
              fileTypes={['image/jpeg', 'image/png']}
              multipleFiles={true}
              handleFiles={handleImageFiles}
            >
              <MenuItem>
                <ListItemIcon style={{ color: 'rgb(162, 172, 180)' }}>
                  <PhotoIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('Photo')}</ListItemText>
              </MenuItem>
            </ReactFileReader>

            <ReactFileReader fileTypes="*" multipleFiles={true} handleFiles={handleFiles}>
              <MenuItem>
                <ListItemIcon style={{ color: 'rgb(162, 172, 180)' }}>
                  <InsertDriveFileIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('File')}</ListItemText>
              </MenuItem>
            </ReactFileReader>
          </Menu>
        </div>
      </div>
    </div>
  );
});
